import React, { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter, Routes as RouterRoutes, Route } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import { LanguageProvider, useLanguage } from "./LanguageContext";
import Layout from "./layout/Layout";

const AdminLogin = lazy(() => import("pages/admin-login"));
const DashboardOverview = lazy(() => import("pages/dashboard-overview"));
const ClientManagement = lazy(() => import("pages/client-management"));
const AddEditClientProfile = lazy(() => import("pages/add-edit-client-profile"));
const SubscriptionManagement = lazy(() => import("pages/subscription-management"));
const AdminAccountManagement = lazy(() => import("pages/admin-account-management"));
const TrainersManagment = lazy(() => import("pages/trainers-managment"));
const AddEditTrainerProfile = lazy(() => import("pages/add-edit-trainer-profile"));
const DepartmentsManagment = lazy(() => import("pages/departments-managment"));
const AddEditDepartmentProfile = lazy(() => import("pages/add-edit-department-profile"));
const EditAppointmentPage = lazy(() => import("pages/appointments-managment/components/EditAppointmentPage"));
const ContactManagment = lazy(() => import("pages/contact-user-managment"));
const GovernoratesManagement = lazy(() => import("pages/governorates-management"));
const ServicesManagement = lazy(() => import("pages/services-management"));
const AddServicePage = lazy(() => import("pages/services-management/components/AddServicePage"));
const EditServicePage = lazy(() => import("pages/services-management/components/EditServicePage"));
const AppointmentsManagement = lazy(() => import("pages/appointments-managment"));
const AddAppointmentPage = lazy(() => import("pages/appointments-managment/components/AddAppointmentPage"));
const LandingPage = lazy(() => import("pages/landing-page"));

const RouteFallback = () => (
  <div className="flex min-h-screen items-center justify-center bg-[#FDF9F5] px-6 text-center text-sm font-semibold text-[#1C5302]">
    Loading…
  </div>
);

const Routes = () => {
  const { direction } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    document.documentElement.dir = direction;
  }, [direction]);

  const toggleSidebar = () => {
    setIsOpen(!isOpen);
  };

  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ScrollToTop />
        <Suspense fallback={<RouteFallback />}>
          <RouterRoutes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/admin-login" element={<AdminLogin />} />
            <Route
              path="/dashboard-overview"
              element={
                <Layout isOpen={isOpen} onClose={toggleSidebar}>
                  <DashboardOverview />
                </Layout>
              }
            />
            <Route
              path="/client-management"
              element={
                <Layout isOpen={isOpen} onClose={toggleSidebar}>
                  <ClientManagement />
                </Layout>
              }
            />
            <Route
              path="/governorates-management"
              element={
                <Layout isOpen={isOpen} onClose={toggleSidebar}>
                  <GovernoratesManagement />
                </Layout>
              }
            />
            <Route
              path="/services-management"
              element={
                <Layout isOpen={isOpen} onClose={toggleSidebar}>
                  <ServicesManagement />
                </Layout>
              }
            />
            <Route path="/add-service" element={<AddServicePage />} />
            <Route path="/edit-service/:id" element={<EditServicePage />} />
            <Route path="/edit-booking/:id" element={<EditAppointmentPage />} />
            <Route
              path="/categories"
              element={
                <Layout isOpen={isOpen} onClose={toggleSidebar}>
                  <TrainersManagment />
                </Layout>
              }
            />
            <Route
              path="/add-edit-client-profile"
              element={
                <Layout isOpen={isOpen} onClose={toggleSidebar}>
                  <AddEditClientProfile />
                </Layout>
              }
            />
            <Route
              path="/add-edit-category"
              element={
                <Layout isOpen={isOpen} onClose={toggleSidebar}>
                  <AddEditTrainerProfile />
                </Layout>
              }
            />
            <Route
              path="/subscription-management"
              element={
                <Layout isOpen={isOpen} onClose={toggleSidebar}>
                  <SubscriptionManagement />
                </Layout>
              }
            />
            <Route
              path="/admin-account-management"
              element={
                <Layout isOpen={isOpen} onClose={toggleSidebar}>
                  <AdminAccountManagement />
                </Layout>
              }
            />
            <Route
              path="/appointments-management"
              element={
                <Layout isOpen={isOpen} onClose={toggleSidebar}>
                  <AppointmentsManagement />
                </Layout>
              }
            />
            <Route
              path="/add-appointment"
              element={
                <Layout isOpen={isOpen} onClose={toggleSidebar}>
                  <AddAppointmentPage />
                </Layout>
              }
            />
            <Route
              path="/departments"
              element={
                <Layout isOpen={isOpen} onClose={toggleSidebar}>
                  <DepartmentsManagment />
                </Layout>
              }
            />
            <Route
              path="/add-edit-department-profile"
              element={
                <Layout isOpen={isOpen} onClose={toggleSidebar}>
                  <AddEditDepartmentProfile />
                </Layout>
              }
            />
            <Route
              path="/contact_us"
              element={
                <Layout isOpen={isOpen} onClose={toggleSidebar}>
                  <ContactManagment />
                </Layout>
              }
            />
          </RouterRoutes>
        </Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

const App = () => (
  <LanguageProvider>
    <Routes />
  </LanguageProvider>
);

export default App;
